/* What a seed string is — the one rule, in one place.
 *
 * There are three doors into a seed: the URL query, the URL hash, and the
 * text field on the runs panel. Each of them grew its own copy of the rule,
 * and the copies disagreed:
 *
 *   - The hash and the query shared one `decodeURIComponent`, but
 *     `URLSearchParams.get()` percent-decodes already, so the query door
 *     decoded TWICE: `?seed=a%2520b` — the correct encoding of the seed
 *     `a%20b` — silently became `a b`, a different world with a different
 *     save slot, from a link that was written correctly.
 *
 *   - `decodeURIComponent` THROWS on a malformed escape, and `%` is in the
 *     charset below, so `#100%` is a string the game itself can produce. The
 *     call ran at module scope in `main.js`, which meant the URIError aborted
 *     module evaluation before `boot()` existed: a black canvas that
 *     reloading the same URL could not recover.
 *
 *   - The 24-character cap was applied to the whole string, so `!night` — the
 *     suffix that selects the long night — was simply eaten off the end of
 *     any base seed longer than eighteen characters. The player got a valid,
 *     different, DAY world and no message. Generated seeds top out at 17 and
 *     survive; a seed a player wrote does not.
 *
 * The old check for this greped the character class out of the source text
 * and never ran the parse, which is why none of the three was caught.
 */

/** The characters the font can actually draw. A seed is the one string in the
 *  game the player writes, and 12 of the 95 printable ASCII characters have no
 *  glyph — `" $ @ [ \ ] ^ _ ` { | }` — so `my_world_[2026]$` used to render as
 *  `SEED MY?WORLD??2026??` on the title screen. */
export const SEED_OK = /[^a-z0-9 .,!?:;'()/+%*<>=#&-]/g;
/** ...and the same class in the case the input field displays. */
export const SEED_OK_UPPER = /[^A-Z0-9 .,!?:;'()/+%*<>=#&-]/g;
/** How long a base seed may be, before the night suffix. */
export const SEED_MAX = 24;
/** The suffix that asks for the long night. Not part of the world hash. */
export const NIGHT_SUFFIX = '!night';

/* WHAT A SUFFIX IS FOR, AND WHAT `!night` MEASURED.
 *
 * `!night` was the only one, and over 24 seed pairs it moved deaths 5.6 -> 9.3
 * and damage taken 101 -> 140 while wins stayed 12 of 24 either way. It makes
 * the same run harder to survive; it does not change what the run IS. Every
 * room, every door, every charm and every hollow of `alpha!night` is
 * `alpha`'s, because the world hash is computed from the seed with the suffix
 * stripped.
 *
 * The two below use that same plumbing to change the RUN instead of the
 * numbers. They pin `delveForm` in `src/hollows.ts` — the per-world shape the
 * three hollows are dug to — at the two ends of its axis:
 *
 *   !deep    one long spine per hollow. Nearly no loops, the fewest optional
 *            rooms, the Warden a long way in, and almost every room asking
 *            something of you on the way.
 *   !warren  a knot. Loops to choose between, three stash rooms, the most
 *            puzzles, the Warden close by a route you have to find rather
 *            than walk.
 *
 * They are two ends of ONE axis, so a seed wearing both is a contradiction:
 * `MOD_SUFFIXES` order decides, and `!warren` wins. That is a canonical form
 * rather than an error, so `a!deep!warren` and `a!warren!deep` are one world
 * with one save slot.
 *
 * WHAT THEY MAY NOT DO, and what `test/gate.ts` proves at every seed:
 *   - The overworld is untouched. `seedNum` comes off `baseSeed`, so the
 *     tiles, the biomes, the tier gates, the charm order and the shops of
 *     `alpha!warren` ARE `alpha`'s.
 *   - The hollow ROOM COUNT is untouched, because `src/world.ts` sizes the
 *     spoil heap outside each mouth off the same number.
 *   - Every gating guarantee holds, because a form is a shape the generator
 *     can already produce for an unsuffixed seed — pinning the axis picks
 *     between shapes, it does not invent one.
 *   - A seed with no suffix is not changed by a single tile: the mods object
 *     is empty and `delveForm` falls through to the seed's own roll.
 */
/** Every suffix a seed may wear, in the order they are written back. */
export const MOD_SUFFIXES = ['!deep', '!warren', '!night'];
/** ...and the flag each one sets. */
const MOD_FLAG = { '!deep': 'deep', '!warren': 'warren', '!night': 'night' };

/** Split a seed into the part the world is hashed from and the run modifiers
 *  hanging off the end of it. Pure, and total: anything that is not a known
 *  suffix is part of the base. */
export function splitSeed(raw, { upper = false } = {}) {
  let base = String(raw == null ? '' : raw).trim();
  const mods: any = { night: false, deep: false, warren: false };
  // bounded: one pass per suffix, and each pass shortens the string
  for (let guard = 0; guard < MOD_SUFFIXES.length + 1; guard++) {
    const hit = MOD_SUFFIXES.find((s) => {
      const suffix = upper ? s.toUpperCase() : s;
      return base.length > suffix.length && base.endsWith(suffix);
    });
    if (!hit) break;
    mods[MOD_FLAG[hit]] = true;
    base = base.slice(0, -hit.length);
  }
  // one axis, two ends: the broad end wins, so a contradiction is a world
  // rather than an error
  if (mods.warren) mods.deep = false;
  return { base, mods };
}

/** The modifiers a seed asks for. */
export function seedMods(seed) {
  return splitSeed(seed).mods;
}

/** The string the world is hashed from: the seed with its modifiers removed.
 *  `alpha`, `alpha!night` and `alpha!warren!night` all hash the same world. */
export function baseSeed(seed) {
  return splitSeed(seed).base;
}

/** The canonical tail for a set of modifiers. */
function suffixesFor(mods, upper) {
  return MOD_SUFFIXES.filter((s) => mods[MOD_FLAG[s]])
    .map((s) => (upper ? s.toUpperCase() : s)).join('');
}

/** Percent-decode without ever throwing. A URL that will not decode is not a
 *  reason to lose the game; it is a reason to take the characters as typed. */
export function decodeSeed(raw) {
  try { return decodeURIComponent(String(raw)); } catch (err) { return String(raw); }
}

/** The canonical seed for a string from any door: filtered to what can be
 *  drawn, capped, and with every modifier suffix preserved across the cap and
 *  written back in one fixed order. */
export function cleanSeed(raw, { upper = false } = {}) {
  const s = String(raw == null ? '' : raw).trim();
  const filtered = upper
    ? s.toUpperCase().replace(SEED_OK_UPPER, '')
    : s.toLowerCase().replace(SEED_OK, '');
  const { base, mods } = splitSeed(filtered, { upper });
  return base.slice(0, SEED_MAX) + suffixesFor(mods, upper);
}

/** The seed a URL is asking for. `qs` is `URLSearchParams.get('seed')` (already
 *  percent-decoded, or null) and `hash` is `location.hash` (not decoded). */
export function seedFromUrl(qs, hash) {
  const raw = qs !== null && qs !== undefined ? qs : decodeSeed(String(hash || '').replace('#', ''));
  return cleanSeed(raw);
}
